// Recording Studio — Voice Actor records CharSpeechSample variations for accepted applications
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Container,
  Grid,
  Header,
  Segment,
  Message,
  Loader,
  Button,
  Icon,
  Label,
  Popup,
  Table,
  Progress,
  TextArea,
  Form,
  Dimmer,
  Pagination
} from 'semantic-ui-react';
import { apiClient } from '../../../apiTools/apiClient';
import { config } from '../../../constants/config';
import { HeaderAndPage } from '../../../common/components';
import { AuthStdFooter } from '../../../utils/auth-std-footer';
import DashBoardSideBar from '../../../utils/dashboard-sidebar';
import { ReactComponent as GearsAndBook } from '../../../assets/gearsAndBook.svg';
import { uploadMedia } from '../../../utils/mediaUploadUtils';
import { fetchUserSignedUrlPathV2 } from '../../../utils/fetchSignedUrlByFolderV2';
import { sendMessage } from '../../../slices/messageSlice';
// Phase 4D — VA Fidelity Gate (P2). Inline panel shows GREEN/AMBER/RED/GRAY
// scoring of the just-recorded take before the VA commits to Save. The Mockup
// component is intentionally pure-presentational; we're re-using it as the
// production component (no rename yet — see RECORDINGSTUDIO_ADDENDA Δ-rewrite
// step 2; the cosmetic lift to `FidelityPanel/` is deferred to keep the diff
// surgical).
import FidelityPanel from '../../../components/voiceFidelity/FidelityPanelMockup/FidelityPanelMockup';
import {
  scoreVoiceSnippet,
  clearFidelityScore,
  clearAllFidelityScores,
} from '../../../slices/voiceFidelitySlice';

// Phase 4D — Recording Studio Mic Toolkit. Reusable hook + presentational
// components that fix the silent-USB-mic capture bug (disable Chrome's implicit
// AGC/EC/NS), add a device picker + live level meter, and hard-block silent
// takes client-side before any upload / Whisper scoring call.
import useStudioRecorder from '../../../hooks/voiceActor/useStudioRecorder';
import MicPicker from '../../../components/voiceRecorder/MicPicker';
import MicLevelMeter from '../../../components/voiceRecorder/MicLevelMeter';

import microphoneStand from '../../../assets/microphone-stand.svg';


// Import styles
import '../../../styles/landing-pages.css';
import '../../../styles/article.css';

// Where the in-studio "ℹ️ Mic not working?" link points: the Authorator
// Information article "Recording Studio: Making a Successful Recording"
// (shell entry uuid below). Its body is the sanitizer-safe HTML at
// public/voice-actor-recording-guide.html, mirrored from the canonical doc
// docs/voice-actor-guide/successful-recording.md. Same-tab navigation (the
// link has no target="_blank") so the VA can use the browser Back button to
// return to the studio. Single constant = one place to change.
const VA_RECORDING_GUIDE_URL =
  '/articleItemContainer/38e40399-010d-4c4d-b2fe-c3287239bf87';



// ============================================================================
// INLINE RECORDER COMPONENT — Phase 4D Mic Toolkit edition
// ============================================================================
/**
 * InlineRecorder — owns the mic UI (device picker, live level meter, countdown,
 * transport, and post-record playback chrome). All of the gnarly capture
 * lifecycle now lives in `useStudioRecorder()` so this component is thin glue.
 *
 * Stays presentational w.r.t. fidelity scoring: it knows nothing about prompts,
 * sample uuids, or scoring envelopes.
 *
 * Phase 4D Mic Toolkit behavior:
 *   - getUserMedia is acquired with AGC/EC/NS DISABLED (fixes the silent
 *     USB-mic capture bug). See useStudioRecorder Fix A.
 *   - A SILENT take is HARD-BLOCKED here: we do NOT forward it to the parent's
 *     `onAudioCaptured`, so no S3 upload and no `/voice-snippets/score` Whisper
 *     call happen. The VA can still re-listen to the blank take (per product
 *     decision) and then Discard & re-record.
 *
 * Props (unchanged contract):
 *   onRecordingComplete(blob, durationSecs)
 *       Legacy internal-Save callback (only used when disableInternalSave=false).
 *   onAudioCaptured(blob, durationSecs)
 *       Fires the instant a NON-SILENT take exists. Parent caches it + scores it.
 *   disableInternalSave : bool
 *       When true, hide the recorder's own Save button (parent's FidelityPanel
 *       drives save instead).
 *   existingUrl : string
 *       A pre-existing recording URL to preview on mount.
 */
const InlineRecorder = ({
  onRecordingComplete,
  existingUrl,
  onAudioCaptured,
  disableInternalSave = false,
}) => {
  // The capture verdict for the most recent take is read off the hook's
  // `silenceResult`; the consumer-side forwarding decision lives in onCapture.
  const onCapture = useCallback(({ blob, duration: dur, silenceResult }) => {
    // HARD-BLOCK silent takes: never forward to the parent (no upload / score).
    // The blank take's <audio> player stays visible so the VA can confirm.
    if (silenceResult?.isSilent) {
      // eslint-disable-next-line no-console
      console.warn(
        '[InlineRecorder] Silent take blocked client-side (maxRms %s dBFS, peak %s dBFS) — not uploaded/scored.',
        Number.isFinite(silenceResult.maxRmsDbfs) ? silenceResult.maxRmsDbfs.toFixed(1) : '-Inf',
        Number.isFinite(silenceResult.peakDbfs) ? silenceResult.peakDbfs.toFixed(1) : '-Inf'
      );
      return;
    }

    if (onAudioCaptured) {
      try {
        onAudioCaptured(blob, dur);
      } catch (cbErr) {
        // eslint-disable-next-line no-console
        console.error('[InlineRecorder] onAudioCaptured threw:', cbErr);
      }
    }
  }, [onAudioCaptured]);

  const {
    phase,
    countdown,
    duration,
    audioBlob,
    audioUrl,
    silenceResult,
    devices,
    selectedDeviceId,
    setSelectedDeviceId,
    permissionState,
    level,
    band,
    startRecording,
    stopRecording,
    cancelCountdown,
    reset,
  } = useStudioRecorder({ existingUrl, onCapture });

  const streamActive = phase === 'countdown' || phase === 'recording';
  const isSilentTake = phase === 'idle' && !!audioUrl && silenceResult?.isSilent;

  const handleSave = () => {
    if (audioBlob && onRecordingComplete) {
      onRecordingComplete(audioBlob, parseFloat(duration));
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {/* Mic device picker + live level meter (Fix B + Fix F) */}
      <MicPicker
        devices={devices}
        selectedDeviceId={selectedDeviceId}
        onChange={setSelectedDeviceId}
        disabled={streamActive}
        helpHref={VA_RECORDING_GUIDE_URL}
      />
      <MicLevelMeter level={level} band={band} active={streamActive} />

      {/* Permission-state UI (see toolkit §3). granted/unknown = silent. */}
      {permissionState === 'prompt' && (
        <Message info size="tiny" style={{ margin: 0, padding: '6px 10px' }}>
          <Icon name="info circle" /> When your browser asks, click <strong>Allow</strong> to use your microphone.
        </Message>
      )}
      {permissionState === 'denied' && (
        <Message negative size="tiny" style={{ margin: 0, padding: '6px 10px' }}>
          <Icon name="microphone slash" /> Microphone is blocked for this site. Click the 🔒/camera icon in your
          browser's address bar → allow the microphone, then reload.{' '}
          <a href={VA_RECORDING_GUIDE_URL}>See the guide</a>.

        </Message>
      )}

      {/* Transport + playback row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        {/* Idle state — show Record button */}
        {phase === 'idle' && (
          <Button
            icon="microphone"
            color="red"
            size="small"
            content="Record"
            disabled={permissionState === 'denied'}
            onClick={startRecording}
          />
        )}

        {/* Countdown state — show countdown + cancel */}
        {phase === 'countdown' && (
          <>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              backgroundColor: '#f44336',
              color: '#fff',
              fontSize: '1.6em',
              fontWeight: 'bold',
              animation: 'pulse 1s infinite'
            }}>
              {countdown}
            </div>
            <span style={{ fontWeight: 'bold', color: '#f44336', fontSize: '1.1em' }}>
              Get ready...
            </span>
            <Button
              icon="cancel"
              size="small"
              basic
              content="Cancel"
              onClick={cancelCountdown}
            />
          </>
        )}

        {/* Recording state — show Stop button + timer */}
        {phase === 'recording' && (
          <>
            <Button
              icon="stop"
              color="grey"
              size="small"
              content={`Stop (${duration}s)`}
              onClick={stopRecording}
            />
            <Label color="red" size="small">
              <Icon name="circle" /> Recording...
            </Label>
          </>
        )}

        {/* Playback + Save — show after recording is complete.
            When `disableInternalSave` is true (Phase 4D fidelity-gated flow)
            the recorder still renders the <audio> preview but hides its own
            Save button — the parent's FidelityPanel owns Save instead.
            A SILENT take never reaches the Save/score path: instead we show a
            warning + Discard & re-record (the player is kept so the VA can
            confirm the silence for themselves). */}
        {audioUrl && phase === 'idle' && (
          <>
            <audio controls src={audioUrl} style={{ height: '32px', maxWidth: '200px' }} />
            {!isSilentTake && audioBlob && !disableInternalSave && (
              <Button
                icon="save"
                color="green"
                size="small"
                content="Save"
                onClick={handleSave}
              />
            )}
            {isSilentTake && (
              <Button
                icon="redo"
                color="orange"
                size="small"
                content="Discard & re-record"
                onClick={reset}
              />
            )}
          </>
        )}
      </div>

      {/* Silent-take warning (Fix C). Energy-based verdict only — content
          agnostic. Kept below the player so the VA can press play first. */}
      {isSilentTake && (
        <Message warning size="tiny" style={{ margin: 0, padding: '8px 10px' }}>
          <Message.Header style={{ fontSize: '0.95em' }}>
            <Icon name="warning sign" /> No sound detected in this take
          </Message.Header>
          <p style={{ marginTop: '4px' }}>
            Your microphone didn&apos;t pick up any audio — this recording is silent. Play it back to confirm,
            then check that the right mic is selected above, watch the level meter while you talk
            (aim for <strong>Good</strong>, not <strong>Silent</strong>/<strong>Quiet</strong>), and try again.
            If you&apos;re on a USB mic, turn its hardware gain up from zero.
          </p>
        </Message>
      )}
    </div>
  );
};


// ============================================================================
// MAIN RECORDING STUDIO COMPONENT
// ============================================================================
const RecordingStudio = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { applicationId } = useParams();
  const currentUser = useSelector(state => state.user.profile?.data);
  const userId = currentUser?.userId;

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [samples, setSamples] = useState([]);
  const [expandedRow, setExpandedRow] = useState(null);
  const [editedText, setEditedText] = useState({});
  const [savingStates, setSavingStates] = useState({});
  const [submittingStates, setSubmittingStates] = useState({});
  const [notification, setNotification] = useState(null);
  const [playingUuid, setPlayingUuid] = useState(null);
  const [loadingAudioUuid, setLoadingAudioUuid] = useState(null);
  const [activePage, setActivePage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'waiting' | 'recorded'
  const itemsPerPage = 10;
  const audioPlayerRef = useRef(null);

  // ── Phase 4D — VA Fidelity Gate (P2 client) ───────────────────────────────
  // Per-sample cache of the most recently captured take. Keyed by sample.uuid
  // → `{ blob, duration }`. The blob is handed to the parent the instant
  // MediaRecorder fires `onstop` (via InlineRecorder.onAudioCaptured), then
  // re-used when the FidelityPanel's "Save & Submit" button is clicked.
  //
  // We deliberately do NOT live in Redux: WebM `Blob`s are non-serializable
  // and rehydrating them on tab-restore is meaningless (the mic stream is
  // long gone). The fidelity *envelope* itself does live in Redux —
  // `state.voiceFidelity[uuid]` — because it's a small, serializable JSON
  // object and selectors elsewhere may want to read it.
  const [capturedTakes, setCapturedTakes] = useState({});

  // Bumping the int for a given sample uuid forces React to remount the
  // <InlineRecorder> for that row by changing its `key`. We use this as the
  // "Re-record" reset signal — it's simpler than threading an imperative
  // `recorder.reset()` handle through useImperativeHandle, and it gives us
  // a free guarantee that all of InlineRecorder's internal refs / timers /
  // streams are torn down by React's unmount path.
  const [recorderResetKey, setRecorderResetKey] = useState({});

  // Whole-subtree selector. Indexing happens inline at render time below.
  // Selectors-per-row would technically be cleaner, but each row would then
  // need to be a sub-component (rules of hooks), which is more refactor than
  // this commit warrants. Re-render cost is negligible — voiceFidelity only
  // updates when a scoring thunk transitions phase.
  const fidelityByUuid = useSelector(state => state.voiceFidelity) || {};

  // Best-effort cleanup on unmount. The slice is sample-scoped (not
  // application-scoped), so leaving rows in `state.voiceFidelity` after the
  // studio closes would slowly leak stale envelopes across navigation cycles.
  useEffect(() => {
    return () => {
      dispatch(clearAllFidelityScores());
    };
  }, [dispatch]);

  /**
   * onAudioCaptured handler (Phase 4D Δ4) — called by InlineRecorder as soon
   * as a take exists. Caches the blob locally and kicks off server-side
   * scoring. The kicked-off thunk transitions state.voiceFidelity[uuid] from
   * undefined → { status:'scoring' } → { status:'scored', envelope }.
   */
  const handleAudioCaptured = useCallback((sample, blob, audioDuration) => {
    const sampleUuid = sample.uuid;
    const promptText = editedText[sampleUuid] !== undefined
      ? editedText[sampleUuid]
      : sample.textContent;

    setCapturedTakes(prev => ({
      ...prev,
      [sampleUuid]: { blob, duration: audioDuration },
    }));

    // Fire-and-forget. Errors are surfaced via state.voiceFidelity[uuid].status
    // === 'error' which the FidelityPanel renders as a soft inline warning.
    dispatch(scoreVoiceSnippet({
      audioBlob: blob,
      promptText,
      sampleUuid,
    }));
  }, [dispatch, editedText]);

  /**
   * Panel "Re-record" — clear the fidelity envelope, evict the cached take,
   * and bump the recorder's reset key so the InlineRecorder remounts clean.
   * The VA can then press Record again on a fresh widget.
   */
  const handleFidelityReRecord = useCallback((sampleUuid) => {
    dispatch(clearFidelityScore({ sampleUuid }));
    setCapturedTakes(prev => {
      const next = { ...prev };
      delete next[sampleUuid];
      return next;
    });
    setRecorderResetKey(prev => ({
      ...prev,
      [sampleUuid]: (prev[sampleUuid] || 0) + 1,
    }));
  }, [dispatch]);

  // Forward declaration — handleFidelitySave is defined after handleSaveRecording
  // below; we wire the panel callback as an arrow so hoisting isn't required.



  // Fetch recording tasks
  const fetchRecordingTasks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const url = config.GET_RECORDING_TASKS.replace(':applicationId', applicationId);
      const response = await apiClient.get(url);
      const result = response.data || response;
      setData(result);
      setSamples(result.samples || []);
    } catch (err) {
      console.error('[RecordingStudio] Error fetching tasks:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load recording tasks');
    } finally {
      setLoading(false);
    }
  }, [applicationId]);

  useEffect(() => {
    if (applicationId) {
      fetchRecordingTasks();
    }
  }, [applicationId, fetchRecordingTasks]);

  // Toggle row expansion
  const toggleRow = (sampleUuid) => {
    setExpandedRow(prev => prev === sampleUuid ? null : sampleUuid);
  };

  // Handle text editing
  const handleTextChange = (sampleUuid, value) => {
    setEditedText(prev => ({ ...prev, [sampleUuid]: value }));
  };

  // Show notification
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  };

  // Handle save recording
  const handleSaveRecording = async (sample, audioBlob, audioDuration) => {
    const sampleUuid = sample.uuid;
    setSavingStates(prev => ({ ...prev, [sampleUuid]: true }));

    try {
      const text = editedText[sampleUuid] !== undefined ? editedText[sampleUuid] : sample.textContent;

      // 1. Upload text file to S3
      const textBlob = new Blob([text], { type: 'text/plain' });
      const textFileName = `${sample.name || 'sample'}_${sampleUuid.slice(0, 8)}.txt`;
      const textFile = new File([textBlob], textFileName, { type: 'text/plain' });

      const textS3Url = await uploadMedia(
        textFile,
        'bookVariationSpeechSampleText',
        {
          litGroupId: data.litGroup.uuid,
          bookId: data.book.uuid,
          speechSampleId: sampleUuid,
          folderName: 'text'
        },
        userId
      );
      console.log('[RecordingStudio] Text uploaded:', textS3Url);

      // 2. Upload voice recording to S3
      //
      // Historical note: we previously saved this File with a `.wav` name and
      // `audio/wav` MIME type. That was a lie — browser `MediaRecorder` emits
      // WebM/Opus (see `new MediaRecorder({ mimeType: 'audio/webm' })` above).
      // The server-side Phase-2 normalizer will transcode the WebM bytes to
      // canonical 16-bit PCM WAV after upload, at which point `VoiceSnippet.s3Url`
      // will point at the true WAV. Until then, be honest about what we uploaded
      // so S3 stores the correct Content-Type and downstream MIME sniffing
      // (ElevenLabs, presigned <audio>, etc.) doesn't have to guess.
      const voiceFileName = `${sample.name || 'sample'}_${sampleUuid.slice(0, 8)}.webm`;
      const voiceFile = new File([audioBlob], voiceFileName, { type: 'audio/webm' });

      const voiceS3Url = await uploadMedia(
        voiceFile,
        'bookVariationSpeechSampleVoice',
        {
          litGroupId: data.litGroup.uuid,
          bookId: data.book.uuid,
          speechSampleId: sampleUuid,
          folderName: 'voice'
        },
        userId
      );
      console.log('[RecordingStudio] Voice uploaded:', voiceS3Url);

      // 3. Update the CharSpeechSample VoiceSnippet s3Url in Neo4j
      const updateUrl = config.UPDATE_SPEECH_SAMPLE_VOICE_URL.replace(':sampleId', sampleUuid);
      await apiClient.put(updateUrl, { s3Url: voiceS3Url });

      // 4. Update local state
      setSamples(prev => prev.map(s =>
        s.uuid === sampleUuid
          ? { ...s, voiceS3Url, textContent: text, status: 'recorded', fileDuration: audioDuration }
          : s
      ));

      setExpandedRow(null);
      showNotification(`Recording saved for "${sample.name}"!`);
    } catch (err) {
      console.error('[RecordingStudio] Save error:', err);
      showNotification(`Error saving recording: ${err.message}`, 'error');
    } finally {
      setSavingStates(prev => ({ ...prev, [sampleUuid]: false }));
    }
  };

  // Handle submit recording — marks sample as submitted and notifies book creator
  const handleSubmitRecording = async (sample) => {
    const sampleUuid = sample.uuid;
    setSubmittingStates(prev => ({ ...prev, [sampleUuid]: true }));

    try {
      // 1. Mark the sample as submitted on the backend
      const submitUrl = config.SUBMIT_SPEECH_SAMPLE.replace(':sampleId', sampleUuid);
      await apiClient.put(submitUrl);

      // 2. Send a notification message to the book creator
      const creatorUserId = data?.creator?.userId;
      if (creatorUserId && userId) {
        const actorName = currentUser?.name || currentUser?.displayName || 'Voice Actor';
        const msgBody = [
          `Recording submitted for review:`,
          `• Variation: ${sample.name}`,
          `• Emotion: ${sample.emotion} (${sample.intensity})`,
          `• Character: ${data?.character?.name || 'Unknown'}`,
          `• Book: ${data?.book?.title || 'Unknown'}`,
          `• Literary Group: ${data?.litGroup?.name || 'Unknown'}`,
          ``,
          `Submitted by ${actorName}`
        ].join('\n');

        dispatch(sendMessage({
          senderId: userId,
          recipientId: creatorUserId,
          content: msgBody,
          messageType: 'RECORDING_SUBMISSION',
          relatedEntityId: applicationId
        }));
      }

      // 3. Update local state to show submitted status
      setSamples(prev => prev.map(s =>
        s.uuid === sampleUuid
          ? { ...s, status: 'submitted' }
          : s
      ));

      showNotification(`"${sample.name}" submitted to book creator for review!`);
    } catch (err) {
      console.error('[RecordingStudio] Submit error:', err);
      showNotification(`Error submitting recording: ${err.message}`, 'error');
    } finally {
      setSubmittingStates(prev => ({ ...prev, [sampleUuid]: false }));
    }
  };

  // Calculate progress
  const totalSamples = samples.length;
  const recordedCount = samples.filter(s => s.status === 'recorded').length;

  // PROGRESS IS AGAINST THE PLAN, not against everything the character can perform.
  //
  // The old bar ran to `samples.length` — every variation in the graph — so a nine-passage
  // assignment showed as 0% of 73 and could never reach 100 without recording sixty-four takes
  // nobody asked for. What the actor owes is the in-plan set, and done means non-waiting:
  // submitted counts, because the actor's part is finished the moment it leaves their hands,
  // whether or not the Book Creator has reviewed it yet.
  const requiredSamples = samples.filter(s => s.inPlan !== false);
  const requiredTotal = requiredSamples.length;
  const requiredDone = requiredSamples.filter(s => s.status !== 'waiting').length;
  const progressPercent = requiredTotal > 0
    ? Math.round((requiredDone / requiredTotal) * 100)
    : 0;

  // ⭐ ORDER BY THE RECORDING PLAN, not by whatever order the graph returned.
  //
  // The Book Creator's planner decides which variations go into this character's pooled voice —
  // ranked by how often the emotion is actually spoken, cut at the 150s clone budget, or picked
  // by hand. The server marks each sample `inPlan` using the SAME selector the mint uses, so the
  // set an actor records is the set that gets cloned.
  //
  // In-plan first, then by lines spoken. Out-of-plan rows are kept and shown dimmed rather than
  // hidden: an actor who wants to record extra should be able to, and a hidden row cannot explain
  // why it is not being asked for.
  const planOrdered = React.useMemo(() => {
    const copy = [...samples];
    copy.sort((a, b) =>
      (b.inPlan ? 1 : 0) - (a.inPlan ? 1 : 0) ||
      (Number(b.occurrenceCount) || 0) - (Number(a.occurrenceCount) || 0) ||
      String(a.emotion || '').localeCompare(String(b.emotion || '')));
    return copy;
  }, [samples]);

  // Filter by status
  // Waiting counts only what is REQUIRED and still outstanding — in-plan and not yet recorded.
  //
  // It used to count every unrecorded variation the character has, which for a big role is dozens
  // that nobody is asking for. "Waiting (73)" beside a nine-passage plan reads as an enormous
  // backlog and is, in the only sense that matters, wrong: seventy-three is not the number of
  // things left to do. Out-of-plan rows are still listed and still recordable; they are simply
  // not owed.
  // The complement of `requiredDone`, so the two cannot overlap. A submitted take is done, not
  // waiting — counting it as both is how a progress bar and a badge end up contradicting.
  const waitingCount = requiredSamples.filter(s => s.status === 'waiting').length;
  const filteredSamples = statusFilter === 'all'
    ? planOrdered
    : statusFilter === 'recorded'
      ? planOrdered.filter(s => s.status === 'recorded')
      : planOrdered.filter(s => s.status !== 'recorded');
  const filteredTotal = filteredSamples.length;

  // Pagination (applied to filtered results)
  const totalPages = Math.ceil(filteredTotal / itemsPerPage);
  const currentSamples = filteredSamples.slice(
    (activePage - 1) * itemsPerPage,
    activePage * itemsPerPage
  );
  const handlePageChange = (e, { activePage }) => {
    setActivePage(activePage);
    setExpandedRow(null);
  };
  const handleFilterChange = (filter) => {
    setStatusFilter(filter);
    setActivePage(1); // reset to page 1 when filter changes
    setExpandedRow(null);
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  // Build subheader text based on loaded data
  const subHeaderText = data
    ? `${data.litGroup?.name || ''} — ${data.book?.title || ''} — ${data.character?.name || ''}`
    : 'Loading recording tasks...';

  // No-op menu handler (sidebar navigation handled by route links)
  const handleMenuSelection = useCallback((menuId) => {
    // Navigate based on menu selection
    switch (menuId) {
      case 'MY-ASSIGNMENTS':
        navigate('/dashboard', { state: { menuSelection: 'MY-ASSIGNMENTS' } });
        break;
      default:
        navigate('/dashboard', { state: { menuSelection: menuId } });
        break;
    }
  }, [navigate]);

  // Build the page content based on state
  const renderContent = () => {
    if (loading) {
      return (
        <Container style={{ padding: '3em 0' }}>
          <Dimmer active inverted>
            <Loader size="large">Loading Recording Tasks...</Loader>
          </Dimmer>
        </Container>
      );
    }

    if (error) {
      return (
        <Container style={{ padding: '3em 0' }}>
          <Message negative icon>
            <Icon name="warning sign" />
            <Message.Content>
              <Message.Header>Error Loading Recording Studio</Message.Header>
              <p>{error}</p>
              <Button as={Link} to="/voice-actor" primary>
                Back to Dashboard
              </Button>
            </Message.Content>
          </Message>
        </Container>
      );
    }

    return (
      <Container fluid style={{ padding: '0 1em' }}>
        {/* Notification */}
        {notification && (
          <Message
            positive={notification.type === 'success'}
            negative={notification.type === 'error'}
            onDismiss={() => setNotification(null)}
            style={{ position: 'fixed', top: '70px', right: '20px', zIndex: 1000, maxWidth: '400px' }}
          >
            <p>{notification.message}</p>
          </Message>
        )}

        {/* Context bar with back button */}
        <Segment clearing style={{ marginBottom: '1em' }}>
          <Grid stackable>
            <Grid.Row verticalAlign="middle">
              <Grid.Column width={11}>
                <Label size="medium" color="blue">
                  <Icon name="user" /> {data?.character?.name}
                </Label>
                <Label size="medium" color="teal">
                  <Icon name="book" /> {data?.book?.title}
                </Label>
                <Label size="medium" color="grey">
                  <Icon name="folder" /> {data?.litGroup?.name}
                </Label>
              </Grid.Column>
              <Grid.Column width={5} textAlign="right">
                <Button as={Link} to={`/applications/${applicationId}`} icon labelPosition="left" size="small">
                  <Icon name="arrow left" /> Back to Application
                </Button>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Segment>

        {/* Progress Bar */}
        <Segment>
          <Grid>
            <Grid.Row verticalAlign="middle">
              <Grid.Column width={3}>
                <Header as="h4">
                  <Icon name="tasks" />
                  Progress: {requiredDone} / {requiredTotal}
                  {requiredTotal < totalSamples && (
                    <span style={{ color: '#999', fontWeight: 'normal', marginLeft: '6px' }}>
                      required · {totalSamples - requiredTotal} excluded
                    </span>
                  )}
                </Header>
              </Grid.Column>
              <Grid.Column width={13}>
                <Progress
                  percent={progressPercent}
                  progress
                  color={progressPercent === 100 ? 'green' : progressPercent > 50 ? 'blue' : 'orange'}
                  size="medium"
                  style={{ marginBottom: 0 }}
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Segment>

        {/* Status Filter */}
        {totalSamples > 0 && (
          <div style={{ marginBottom: '1em', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontWeight: 'bold', marginRight: '4px' }}>Filter:</span>
            <Button.Group size="small">
              <Button
                active={statusFilter === 'all'}
                primary={statusFilter === 'all'}
                onClick={() => handleFilterChange('all')}
              >
                All ({totalSamples})
              </Button>
              <Button
                active={statusFilter === 'waiting'}
                color={statusFilter === 'waiting' ? 'yellow' : undefined}
                onClick={() => handleFilterChange('waiting')}
              >
                <Icon name="clock" /> Waiting ({waitingCount})
              </Button>
              <Button
                active={statusFilter === 'recorded'}
                color={statusFilter === 'recorded' ? 'green' : undefined}
                onClick={() => handleFilterChange('recorded')}
              >
                <Icon name="check" /> Recorded ({recordedCount})
              </Button>
            </Button.Group>
          </div>
        )}

        {/* Samples Table */}
        {filteredTotal === 0 && totalSamples > 0 ? (
          <Message info>
            <Message.Header>No Matching Samples</Message.Header>
            <p>No samples match the &quot;{statusFilter}&quot; filter. Try selecting a different filter above.</p>
          </Message>
        ) : totalSamples === 0 ? (
          <Message info>
            <Message.Header>No Recording Tasks Found</Message.Header>
            <p>There are no CharSpeechSample variations to record for this application&apos;s character.</p>
          </Message>
        ) : (
          <Table celled structured>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell width={3}>Variation</Table.HeaderCell>
                <Table.HeaderCell width={2} textAlign="center">Emotion</Table.HeaderCell>
                <Table.HeaderCell width={1}>Intensity</Table.HeaderCell>
                <Table.HeaderCell width={2} textAlign="center"
                  title="How often the character speaks this emotion, and how long the passage runs. The Book Creator's plan is ranked on the first and budgeted on the second.">
                  Weight
                </Table.HeaderCell>
                <Table.HeaderCell width={3}>Text to Read</Table.HeaderCell>
                <Table.HeaderCell width={2} textAlign="center">Status</Table.HeaderCell>
                <Table.HeaderCell width={5} textAlign="center">Actions</Table.HeaderCell>
              </Table.Row>
            </Table.Header>

            <Table.Body>
              {currentSamples.map((sample) => (
                <React.Fragment key={sample.uuid}>
                  {/* Main Row */}
                  <Table.Row
                    positive={sample.status === 'recorded'}
                    warning={sample.status === 'waiting'}
                    style={sample.inPlan === false ? { opacity: 0.5 } : undefined}
                  >
                    <Table.Cell>
                      <strong>{sample.name}</strong>
                      {/* Dimmed and labelled rather than hidden. An actor may still want to
                        * record one, and a row that simply vanished could not say why it was
                        * not being asked for. */}
                      {sample.inPlan === false && (
                        <Popup
                          size="mini"
                          content="Excluded from the clone plan — the Book Creator's selection does not include this variation. Recording it is not required."
                          trigger={
                            <Label size="mini" basic style={{ marginLeft: '6px' }}>excluded</Label>
                          }
                        />
                      )}
                    </Table.Cell>
                    <Table.Cell textAlign="center">
                      <Label color="teal">{sample.emotion}</Label>
                    </Table.Cell>
                    <Table.Cell>
                      <Label color="grey">{sample.intensity}</Label>
                    </Table.Cell>
                    {/* Lines spoken and the length of this take. The same two figures the Book
                      * Creator plans with, so a conversation about what to record has the same
                      * numbers on both sides of it. */}
                    <Table.Cell textAlign="center">
                      {Number(sample.occurrenceCount) > 0 && (
                        <span style={{ fontSize: '0.85em', color: '#666', marginRight: '8px' }}>
                          {sample.occurrenceCount} ln
                        </span>
                      )}
                      {sample.planSeconds > 0 && (
                        <span style={{ fontSize: '0.85em', color: '#999' }}>
                          {Number(sample.fileDuration) > 0 ? '' : '~'}
                          {Number(sample.planSeconds).toFixed(1)}s
                        </span>
                      )}
                    </Table.Cell>
                    {/* A PREVIEW, deliberately truncated. The row is for scanning the list; the
                      * full passage lives in the recording panel below, which is where it is
                      * actually read from. */}
                    <Table.Cell verticalAlign="top">
                      <span style={{ fontSize: '0.9em', color: '#555' }}>
                        {(sample.textContent || '').substring(0, 120)}
                        {(sample.textContent || '').length > 120 && '…'}
                      </span>
                    </Table.Cell>
                    <Table.Cell textAlign="center">
                      {sample.status === 'submitted' ? (
                        <Label color="blue" size="small">
                          <Icon name="send" /> Submitted
                        </Label>
                      ) : sample.status === 'recorded' ? (
                        <Label color="green" size="small">
                          <Icon name="check" /> Recorded
                        </Label>
                      ) : (
                        <Label color="yellow" size="small">
                          <Icon name="clock" /> Waiting
                        </Label>
                      )}
                    </Table.Cell>
                    <Table.Cell textAlign="center">
                      <Button.Group size="small">
                        {sample.voiceS3Url && (
                          <Button
                            icon={loadingAudioUuid === sample.uuid ? 'spinner' : (playingUuid === sample.uuid ? 'pause' : 'play')}
                            loading={loadingAudioUuid === sample.uuid}
                            color="green"
                            disabled={loadingAudioUuid === sample.uuid}
                            onClick={async () => {
                              if (playingUuid === sample.uuid) {
                                // Stop playback
                                if (audioPlayerRef.current) {
                                  audioPlayerRef.current.pause();
                                  audioPlayerRef.current.currentTime = 0;
                                }
                                setPlayingUuid(null);
                              } else {
                                try {
                                  // Stop any current playback
                                  if (audioPlayerRef.current) {
                                    audioPlayerRef.current.pause();
                                  }
                                  // Show loading state
                                  setLoadingAudioUuid(sample.uuid);

                                  // Get a signed URL for the S3 object
                                  const signedUrl = await fetchUserSignedUrlPathV2(userId, sample.voiceS3Url);

                                  // Fetch entire file into memory (deterministic — no buffering issues)
                                  const response = await fetch(signedUrl);
                                  const arrayBuffer = await response.arrayBuffer();
                                  const blob = new Blob([arrayBuffer], { type: 'audio/webm' });
                                  const localUrl = URL.createObjectURL(blob);

                                  // Play from in-memory blob — zero clipping
                                  const audio = new Audio(localUrl);
                                  audioPlayerRef.current = audio;
                                  audio.onended = () => {
                                    setPlayingUuid(null);
                                    URL.revokeObjectURL(localUrl);
                                  };
                                  audio.onerror = () => {
                                    console.error('[RecordingStudio] Audio playback error');
                                    setPlayingUuid(null);
                                    setLoadingAudioUuid(null);
                                    URL.revokeObjectURL(localUrl);
                                  };
                                  await audio.play();
                                  setPlayingUuid(sample.uuid);
                                  setLoadingAudioUuid(null);
                                } catch (err) {
                                  console.error('[RecordingStudio] Playback error:', err);
                                  setLoadingAudioUuid(null);
                                  showNotification('Could not play audio. The file may not be accessible.', 'error');
                                }
                              }
                            }}
                          />
                        )}
                        <Button
                          icon={expandedRow === sample.uuid ? 'chevron up' : (sample.status === 'recorded' || sample.status === 'submitted' ? 'redo' : 'microphone')}
                          color={sample.status === 'recorded' || sample.status === 'submitted' ? 'orange' : 'red'}
                          content={expandedRow === sample.uuid ? 'Close' : (sample.status === 'recorded' || sample.status === 'submitted' ? 'Re-record' : 'Record')}
                          onClick={() => toggleRow(sample.uuid)}
                          loading={savingStates[sample.uuid]}
                        />
                      </Button.Group>

                      {/* SUBMIT GETS ITS OWN LINE.
                        *
                        * It was the third button in the group, and three wide labels — Play,
                        * Re-record, Submitted — overran the Actions cell and drew past the table
                        * edge. Putting it below keeps the transport controls (play / record /
                        * re-record) together on one line as a set, which is what they are, and
                        * gives the one button that ADVANCES the sample a line of its own.
                        *
                        * Full width so it reads as a step rather than a fourth transport button. */}
                      {sample.status === 'recorded' && (
                        <Button
                          icon="send"
                          color="blue"
                          content="Submit"
                          size="small"
                          fluid
                          style={{ marginTop: '6px' }}
                          onClick={() => handleSubmitRecording(sample)}
                          loading={submittingStates[sample.uuid]}
                          disabled={submittingStates[sample.uuid]}
                        />
                      )}
                      {sample.status === 'submitted' && (
                        <Button
                          icon="check"
                          color="grey"
                          content="Submitted"
                          size="small"
                          fluid
                          style={{ marginTop: '6px' }}
                          disabled
                        />
                      )}
                    </Table.Cell>
                  </Table.Row>

                  {/* Expanded Recording Row */}
                  {expandedRow === sample.uuid && (
                    <Table.Row>
                      <Table.Cell colSpan="7">
                        <Segment secondary style={{ margin: '0.5em 0' }}>
                          <Grid stackable>
                            <Grid.Row>
                              <Grid.Column width={10}>
                                <Form>
                                  <Form.Field>
                                    <label>Text to Read (editable)</label>
                                    {/* SIZED TO THE PASSAGE. This is the box the actor reads from
                                      * while recording, and it was fixed at three rows — so
                                      * anything past about forty words had to be scrolled
                                      * mid-take, which is exactly when you cannot spare a hand or
                                      * the attention.
                                      *
                                      * Rows are computed from the text rather than auto-sized
                                      * with a ref: no measurement, no resize observer, and it is
                                      * right on first paint instead of snapping a frame later.
                                      * ~68 characters fit a 550px line at 1.1em; authored line
                                      * breaks are counted separately since they end a line early.
                                      * Capped at 24 so a pathological passage cannot swallow the
                                      * page. */}
                                    <TextArea
                                      rows={(() => {
                                        const t = editedText[sample.uuid] !== undefined
                                          ? editedText[sample.uuid]
                                          : (sample.textContent || '');
                                        const wrapped = Math.ceil(t.length / 68);
                                        const breaks = (t.match(/\n/g) || []).length;
                                        return Math.min(24, Math.max(3, wrapped + breaks + 1));
                                      })()}
                                      value={editedText[sample.uuid] !== undefined ? editedText[sample.uuid] : sample.textContent}
                                      onChange={(e) => handleTextChange(sample.uuid, e.target.value)}
                                      style={{ fontSize: '1.1em', lineHeight: '1.6', width: "550px" }}
                                    />
                                  </Form.Field>
                                </Form>
                                {sample.contextText && (
                                  <div style={{ maxWidth: 550, marginTop: 8 }}>
                                    <div style={{
                                      fontSize: '0.72em', fontWeight: 'bold', color: '#aaa',
                                      textTransform: 'uppercase', letterSpacing: '0.05em'
                                    }}>
                                      In-book text — reference only, do not read
                                    </div>
                                    <div style={{
                                      fontStyle: 'italic', color: '#888', fontSize: '0.92em',
                                      borderLeft: '3px solid #e0e0e0', paddingLeft: 10, marginTop: 3
                                    }}>
                                      {sample.contextText}
                                    </div>
                                  </div>
                                )}
                              </Grid.Column>
                              <Grid.Column width={6}>
                                <Header as="h5">
                                  <Icon name="microphone" /> Record Voice
                                </Header>
                                {/* Recorder is **keyed** on the per-sample reset
                                    counter — incrementing it via
                                    handleFidelityReRecord remounts the widget
                                    clean (releases mic stream, clears audio
                                    URL, drops the take). disableInternalSave
                                    is hard-coded true: every Save path now
                                    flows through the FidelityPanel below so
                                    the VA can't skip the scoring step. */}
                                <InlineRecorder
                                  key={`${sample.uuid}-${recorderResetKey[sample.uuid] || 0}`}
                                  existingUrl={sample.voiceS3Url}
                                  onRecordingComplete={(blob, dur) => handleSaveRecording(sample, blob, dur)}
                                  onAudioCaptured={(blob, dur) => handleAudioCaptured(sample, blob, dur)}
                                  disableInternalSave={true}
                                />
                                {/* Fidelity panel — rendered only after a take
                                    has been captured for this sample. The
                                    `status==='scoring'` slice phase yields a
                                    PENDING band so the panel can show its
                                    spinner / skeleton header. Once the thunk
                                    resolves we hand the panel the real
                                    envelope from the slice. */}
                                {fidelityByUuid[sample.uuid] && (() => {
                                  const fid = fidelityByUuid[sample.uuid];
                                  const promptText = editedText[sample.uuid] !== undefined
                                    ? editedText[sample.uuid]
                                    : sample.textContent;

                                  // Pending shape — keep this minimal: the
                                  // panel only reads .fidelityBand to pick
                                  // its color, so a stub with a known band
                                  // is enough to render the placeholder UI.
                                  const envelope = fid.status === 'scoring'
                                    ? { fidelityBand: 'PENDING', fidelityScore: null }
                                    : fid.envelope;

                                  return (
                                    <div style={{ marginTop: '1em' }}>
                                      <FidelityPanel
                                        promptText={promptText}
                                        score={envelope}
                                        onReRecord={() => handleFidelityReRecord(sample.uuid)}
                                        onSaveSubmit={({ vaReason } = {}) => {
                                          // Resolve cached take at click-time
                                          // (not at render time) so we always
                                          // grab the most recent capture even
                                          // if React re-rendered between mount
                                          // and click.
                                          const take = capturedTakes[sample.uuid];
                                          if (!take) {
                                            // Defensive — shouldn't happen
                                            // because the panel only renders
                                            // when fidelityByUuid is populated,
                                            // which only happens after we
                                            // cache a take.
                                            // eslint-disable-next-line no-console
                                            console.warn(
                                              '[RecordingStudio] Save clicked but no cached take for',
                                              sample.uuid
                                            );
                                            return;
                                          }
                                          // P2: vaReason is captured but not
                                          // yet persisted. P3 will wire it
                                          // into the CharSpeechSample
                                          // metadata (see RECORDINGSTUDIO_
                                          // ADDENDA §Persistence punch-list).
                                          if (vaReason) {
                                            // eslint-disable-next-line no-console
                                            console.log(
                                              '[RecordingStudio] vaReason captured (P2 — not yet persisted):',
                                              vaReason
                                            );
                                          }
                                          handleSaveRecording(sample, take.blob, take.duration);
                                        }}
                                      />
                                    </div>
                                  );
                                })()}
                                {savingStates[sample.uuid] && (
                                  <Message info size="small" style={{ marginTop: '0.5em' }}>
                                    <Icon name="spinner" loading /> Saving recording...
                                  </Message>
                                )}
                              </Grid.Column>

                            </Grid.Row>
                          </Grid>
                        </Segment>
                      </Table.Cell>
                    </Table.Row>
                  )}
                </React.Fragment>
              ))}
            </Table.Body>
          </Table>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <span style={{ fontSize: '0.9em', color: '#666', marginBottom: '8px', display: 'block' }}>
              Showing {Math.min((activePage - 1) * itemsPerPage + 1, filteredTotal)}-{Math.min(activePage * itemsPerPage, filteredTotal)} of {filteredTotal} samples
              {statusFilter !== 'all' && ` (filtered from ${totalSamples} total)`}
            </span>
            <Pagination
              activePage={activePage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              ellipsisItem={{ content: '...', icon: 'ellipsis horizontal' }}
              firstItem={{ content: <Icon name="angle double left" />, icon: true }}
              lastItem={{ content: <Icon name="angle double right" />, icon: true }}
              prevItem={{ content: <Icon name="angle left" />, icon: true }}
              nextItem={{ content: <Icon name="angle right" />, icon: true }}
            />
          </div>
        )}

        {/* Completion Message */}
        {progressPercent === 100 && (
          <Message positive icon>
            <Icon name="trophy" />
            <Message.Content>
              <Message.Header>All Recordings Complete!</Message.Header>
              <p>
                You have recorded all {requiredTotal} required variation sample
                {requiredTotal === 1 ? '' : 's'} for {data?.character?.name}.
                {requiredTotal < totalSamples && (
                  <> The other {totalSamples - requiredTotal} are excluded from the clone plan
                    and were not needed.</>
                )}
              </p>
            </Message.Content>
          </Message>
        )}

      </Container>
    );
  };

  return (
    <div className="std-pg-main-area std-pg-grey-background-color" style={{ marginBottom: "70px" }}>
      <div className='dashBoardSidebar-inner'>
        <div className="dashSliderTitle">Dashboard</div>
        <GearsAndBook className="dashboardHeadingIcon" />
        {DashBoardSideBar(handleMenuSelection)}
      </div>

      <div className="dashBoardSubPage">
        <HeaderAndPage
          headerImage={microphoneStand}
          headerText="Recording Studio"
          subHeaderText={subHeaderText}
        >
          {renderContent()}
          <AuthStdFooter />
        </HeaderAndPage>
      </div>

      <div className='dashBoardSidebar-ext' />
    </div>
  );
};

export default RecordingStudio;
